import React, { useEffect, useRef } from "react";
import * as echarts from "echarts";

const GenericBubbleChart = ({
  graph_id,
  graph_data = [],
  graph_height = 400,
  xField, // e.g. "cagr"
  yField, // e.g. "size_billion"
  sizeField, // e.g. "size_billion" (or another metric to scale bubbles)
  nameField, // e.g. "segment_name"
  xAxisLabel = "CAGR",
  yAxisLabel = "SIZE",
}) => {
  const elRef = useRef(null);

  useEffect(() => {
    if (!elRef.current) return;
    const chart = echarts.init(elRef.current);

    // Normalize input: accept array or { data_set: [...] } or { data: [...] }
    const rows = Array.isArray(graph_data)
      ? graph_data
      : graph_data?.data_set || graph_data?.data || [];

    // Safe numeric parse (supports "$12.3B", "12.3%", etc.)
    const num = (v) =>
      typeof v === "number"
        ? v
        : parseFloat(String(v ?? "").replace(/[^0-9.+-]/g, "")) || 0;

    // Build data for series
    const points = rows.map((d) => {
      const x = num(d[xField]); // % (CAGR)
      const y = num(d[yField]); // Billions
      const s = num(d[sizeField]); // bubble size metric
      return {
        value: [x, y, s],
        name: d[nameField],
        // optional color support: d.color or d.bubble_color; fallback beige
        itemStyle: { color: d.color || d.bubble_color || "#e8c98f" },
        ...d,
      };
    });

    // Bubble sizing (scaled between 10–50px)
    const sizes = points.map((p) => p.value[2]);
    const minS = Math.min(...(sizes.length ? sizes : [0]));
    const maxS = Math.max(...(sizes.length ? sizes : [1]));
    const bubbleSize = (v) => {
      if (maxS === minS) return 30;
      const t = (v - minS) / (maxS - minS);
      return 10 + t * 40; // 10 → 50
    };

    // Determine axis ranges a bit padded
    const xs = points.map((p) => p.value[0]);
    const ys = points.map((p) => p.value[1]);
    const minX = Math.min(...(xs.length ? xs : [0]));
    const maxX = Math.max(...(xs.length ? xs : [100]));
    const minY = Math.min(...(ys.length ? ys : [0]));
    const maxY = Math.max(...(ys.length ? ys : [1]));
    const pad = (min, max, pct = 0.05) => {
      const span = max - min || 1;
      return [min - span * pct, max + span * pct];
    };
    const [xMinRaw, xMax] = pad(minX, maxX, 0.04);
    const [yMinRaw, yMax] = pad(minY, maxY, 0.04);

    const xMin = Math.max(0, xMinRaw);
    const yMin = Math.max(0, yMinRaw);

    // decide which axis is CAGR (percent) based on label/field name
    const isXPercent = (xAxisLabel || xField || "")
      .toLowerCase()
      .includes("cagr");
    const isYPercent = (yAxisLabel || yField || "")
      .toLowerCase()
      .includes("cagr");

    const option = {
      grid: { left: 70, right: 20, top: 20, bottom: 55, containLabel: false },

      // X axis: bottom dotted border, % labels, centered title
      xAxis: {
        min: xMin,
        max: xMax,
        name: xAxisLabel,
        nameLocation: "middle",
        nameGap: 30,
        nameTextStyle: {
          color: "black",
          fontWeight: "bold",
        },
        axisLabel: {
          formatter: (v) =>
            isXPercent
              ? `${Number(v).toFixed(0)}%`
              : `${Number(v).toFixed(0)}B`,
          color: "#6b6b6b",
        },
        axisLine: {
          show: true,
          lineStyle: {
            color: "#bfa9ec",
            type: "dashed",
            width: 1,
          },
        },
        axisTick: { show: false },
        splitLine: { show: false },
      },

      // Y axis: 3 colored horizontal bands, B suffix, left dotted border, vertical title
      yAxis: {
        min: yMin,
        max: yMax,
        name: yAxisLabel,
        nameLocation: "middle",
        nameGap: 55,
        nameRotate: 90,
        nameTextStyle: {
          color: "black",
          fontWeight: "bold",
        },
        axisLabel: {
          formatter: (v) =>
            isYPercent
              ? `${Number(v).toFixed(0)}%`
              : `${Number(v).toFixed(0)}B`,
          color: "#6b6b6b",
        },
        axisLine: {
          show: true,
          lineStyle: { color: "#bfa9ec", type: "dashed", width: 1 },
        },
        axisTick: { show: false },
        splitNumber: 3, // exactly 3 bands
        splitLine: { show: false },
        splitArea: {
          show: true,
          areaStyle: {
            // bottom → top
            color: ["#fcf3f4", "#fdfaef", "#f3fbf4"],
          },
        },
      },

      tooltip: {
        trigger: "item",
        backgroundColor: "#fff",
        borderColor: "#bfa9ec",
        borderWidth: 1,
        textStyle: { color: "#333" },
        formatter: (p) => {
          const nm = (p.data?.name || "")
            .toString()
            .replace(/_/g, " ")
            .replace(/\b\w/g, (c) => c.toUpperCase());
          const x = Number(p.value[0]).toFixed(1);
          const y = Number(p.value[1]).toFixed(1);
          const s = p.value[2];
          return `
            <div style="font-size:12px">
              <b>${nm}</b><br/>
              ${xAxisLabel}: ${isXPercent ? `${x}%` : `${x}B`}<br/>
              ${yAxisLabel}: ${isYPercent ? `${y}%` : `${y}B`}<br/>
              </div>`;
          // ${sizeField}: ${s}
        },
      },

      series: [
        {
          type: "scatter",
          data: points,
          symbol: "circle",
          symbolSize: (val) => bubbleSize(val[2]),
          itemStyle: {
            borderColor: "#ffffff",
            borderWidth: 1,
            shadowBlur: 8,
            shadowColor: "rgba(0,0,0,0.15)",
          },
          emphasis: {
            focus: "self",
            itemStyle: { borderColor: "#333", borderWidth: 2 },
          },
        },
      ],
    };

    chart.setOption(option, true);

    const onResize = () => chart.resize();
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      chart.dispose();
    };
  }, [
    graph_data,
    xField,
    yField,
    sizeField,
    nameField,
    xAxisLabel,
    yAxisLabel,
  ]);

  return (
    <div
      id={graph_id}
      ref={elRef}
      style={{ width: "100%", height: graph_height }}
    />
  );
};

export default GenericBubbleChart;
